import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { parseUrl } from 'query-string';
import { getUnpublishedEntityName } from '@blaze-cms/react-page-builder';
import { checkAccessToken } from '@blaze-cms/core-auth-ui';
import { HTTPS, DOUBLE_SLASH, COLON, NOT_FOUND_STATUS_CODE, BLAZE_X_FRAME_OPTIONS_DEFAULT, ROUTE_REGEX, ERROR_URLS } from '../constants';
import { checkUrlQuery } from '../application/query';
import checkParent from './check-parent';
import checkPreviewUrl from './check-preview-url';
import handleStaticRoutes from './handle-static-routes';
import getPageData from './get-page-data';
import getRootSelectorClasses from './get-root-selector-classes';
const getRootClassesLimit = () => Number(process.env.BLAZE_ROOT_SELECTOR_CLASSES_LIMIT || 1);
const checkUrl = async props => {
  const {
    asPath,
    apolloClient,
    res,
    req,
    query,
    blazeApp,
    disableSsr
  } = props;
  const fullUrl = getFullUrl(req, asPath);
  const {
    url
  } = parseUrl(asPath);
  if (process.env.BLAZE_STATUS_URL && url === process.env.BLAZE_STATUS_URL) {
    return {
      isStatusPage: true
    };
  }
  if (res) {
    const handler = handleStaticRoutes.getHandler(asPath);
    res.setHeader('X-Frame-Options', process.env.BLAZE_X_FRAME_OPTIONS || BLAZE_X_FRAME_OPTIONS_DEFAULT);
    if (!res.getHeader('Cache-Control')) {
      res.setHeader('Cache-Control', process.env.BLAZE_CACHE_CONTROL_HEADER || ' ');
    }
    if (handler) {
      return handler(props);
    }
  }
  const previewData = checkPreviewUrl(url, query);
  if (!previewData && disableSsr) return {
    disableSsr: true
  };
  await checkAccessToken(apolloClient, _objectSpread(_objectSpread({}, props), {}, {
    disableSsr: !Object.keys(apolloClient.cache.extract()).length // empty cache means SSR was disabled
  }));
  if (previewData) return getPreviewData(previewData, apolloClient);
  const urlCheckOptions = {
    asPath,
    url,
    apolloClient,
    res
  };
  const result = await doUrlCheck(_objectSpread(_objectSpread({}, urlCheckOptions), {}, {
    handle404Error: true,
    blazeApp
  }));
  return _objectSpread(_objectSpread({}, result), {}, {
    fullUrl
  }) || {};
};
const getPreviewData = async (previewData, apolloClient) => {
  const result = await getPageData(_objectSpread(_objectSpread({}, previewData), {}, {
    apolloClient
  }));
  const {
    pageData: {
      url
    } = {}
  } = result;
  const rootSelectorClasses = getRootSelectorClasses(url, getRootClassesLimit());
  return _objectSpread(_objectSpread(_objectSpread({}, previewData), {}, {
    rootSelectorClasses
  }, result), {}, {
    isPreview: true
  });
};
const getHost = ({
  headers = {}
}) => process.env.BLAZE_FRONTEND_HOST || headers['x-request-host'] || headers['x-forwarded-host'] || headers.host;
const getFullUrl = (req, asPath) => {
  if (req) {
    return `${HTTPS}${getHost(req)}${asPath}`;
  }
  const {
    location: {
      protocol,
      hostname,
      port
    }
  } = window;
  const urlPort = port ? `${COLON}${port}` : '';
  return `${protocol}${DOUBLE_SLASH}${hostname}${urlPort}${asPath}`;
};
const doUrlCheck = async args => {
  const {
    asPath,
    url,
    apolloClient,
    res,
    blazeApp,
    errorCheckCode = false
  } = args;
  try {
    if (!ROUTE_REGEX.test(url)) {
      throw Error('Invalid URL');
    }
    const {
      data: {
        checkUrl: {
          itemId,
          itemEntity,
          urlTo,
          pageData = {}
        } = {}
      } = {}
    } = await apolloClient.query({
      query: checkUrlQuery,
      variables: {
        url
      }
    });
    setCustomCacheControlHeaders(pageData, res);
    if (urlTo && (urlTo !== url || errorCheckCode)) {
      return handleRedirect({
        errorCheckCode,
        url,
        urlTo,
        asPath,
        res
      });
    }
    const rootSelectorClasses = getRootSelectorClasses(url, getRootClassesLimit());
    if (errorCheckCode) {
      blazeApp.events.emit('monitor:log', {
        message: `Serving custom ${errorCheckCode} page`
      });
      if (res) res.statusCode = errorCheckCode;
    }
    return {
      pageData,
      rootSelectorClasses,
      itemId,
      itemEntity: getUnpublishedEntityName(itemEntity)
    };
  } catch (err) {
    return handleUrlError(err, args);
  }
};
const handleError = (err, res, handle404Error, blazeApp) => {
  blazeApp.events.emit('monitor:log', {
    error: err
  });
  if (!res) return {};
  if (err.networkError) {
    res.writeHead(500, {
      'Content-Type': 'text/plain'
    });
    res.write('500 Internal Server Error\n');
    res.end();
  } else if (handle404Error && err.graphQLErrors && err.graphQLErrors.length) {
    res.statusCode = NOT_FOUND_STATUS_CODE;
    return {
      errorCode: NOT_FOUND_STATUS_CODE
    };
  }
  return {};
};
function handleRedirect({
  errorCheckCode,
  url,
  urlTo,
  asPath,
  res
}) {
  if (errorCheckCode) {
    const errorMessage = `Cannot redirect ${url}`;
    const customError = new Error(errorMessage);
    customError.graphQLErrors = [errorMessage];
    throw customError;
  }
  let redirectUrl = urlTo;
  if (process.env.BLAZE_DISABLE_REDIRECT_WITH_QUERY !== 'true') {
    const queryString = asPath.split('?')[1];
    redirectUrl = queryString ? `${urlTo}?${queryString}` : urlTo;
  }
  return doRedirect(res, redirectUrl);
}
function setCustomCacheControlHeaders(pageData, res) {
  const {
    cacheControlMaxAge = null
  } = pageData || {};
  if (res && cacheControlMaxAge !== null && cacheControlMaxAge >= 0) {
    res.setHeader('Cache-Control', `max-age=${cacheControlMaxAge}`);
  }
}
function doRedirect(res, redirectUrl) {
  if (res) {
    res.writeHead(301, {
      Location: redirectUrl
    });
    res.end();
  } else {
    window.location.href = redirectUrl;
  }
  return {
    redirecting: true
  };
}
async function handleUrlError(err, args) {
  const {
    asPath,
    res,
    handle404Error,
    blazeApp,
    errorCheckCode
  } = args;
  if (!err.graphQLErrors || !err.graphQLErrors.length) {
    return handleError(err, res, handle404Error, blazeApp);
  }
  if (!errorCheckCode) {
    const result = await doUrlCheck(_objectSpread(_objectSpread({}, args), {}, {
      url: ERROR_URLS.custom404,
      errorCheckCode: NOT_FOUND_STATUS_CODE
    }));
    if (handle404Error) {
      const parentUrl = checkParent(asPath);
      if (parentUrl) {
        return doRedirect(res, parentUrl);
      }
    }
    return result;
  }
  const parentPATH = checkParent(asPath);
  if (handle404Error && parentPATH) {
    return doRedirect(res, parentPATH);
  }
  return handleError(err, res, handle404Error, blazeApp);
}
export default checkUrl;
//# sourceMappingURL=check-url.js.map